import { useEffect, useRef, useState } from 'react';

import { ensureRegionMap, regionMapRegistered } from './vendor.js';
import { adopt } from '../../../vendor/maps/scenario.js';
import type { MapLayers } from '../model/map-layers.js';
import type { ScenarioModel } from '../model/scenario.js';
import type { MapCommand } from '../state/store.js';

/**
 * The typed React wrapper over `vendor/maps/region-map.js` (M5-D7).
 *
 * Props in → `api` calls; `CustomEvent`s out → callbacks. Nothing else. The
 * renderer is vendored verbatim and this file is the only thing between it and
 * React, which is what keeps a re-vendor a clean diff — and it is the shape
 * Phases 6 and 7 copy for the orbital and cyber boards.
 *
 * The imperative surface, from the renderer's own `boot()`:
 *
 * ```
 * select · focusUnit · focusRegion · setLayers · setDim · setCrossLink
 * setPick · setTerrain · theme · setHover · fitSide · resetView
 * ```
 *
 * and out: `bs-select`, `bs-region`, `bs-pick`, `bs-context`, `bs-hover`,
 * `bs-mod-click`, `bs-engage`. Every one of those has had a handler in
 * `ConsoleShell` since Phases 2 and 3, written against nothing; this is the file
 * that connects them, because a gesture that reaches a handler which does not
 * exist is worse than one that is unbound.
 *
 * ## The scenario facade is adopted elsewhere
 *
 * `region-map.js` imports `./scenario.js` itself, and `mw-map.js` *extends* this
 * element and therefore boots the same way — so the model cannot belong to this
 * component's lifetime. `maps/useTheatreModel.ts` adopts it once for the whole
 * console; this component only re-adopts when the model object changes, which is
 * idempotent and is what makes a snapshot land on the canvas.
 *
 * ## Two calls that are not on `api`, and why
 *
 * `geometry()` re-solves each region's marker slots. The renderer calls it from
 * `resize()` and nowhere else, because the prototype's roster is a static import
 * that never changes; ours changes on every resolution, and without a re-solve a
 * contact that appeared this turn has no slot index at all and lands on top of
 * whoever holds slot 0. It is called only when the *set* of placed units changes,
 * never on a plain redraw — see `slotSignature`.
 *
 * `layout()` redraws the markers after it. Both are guarded, both are on the
 * element rather than on `api`, and both are named here so a re-vendor that
 * renames them fails in one obvious place.
 *
 * ## What this does not draw
 *
 * **A relocation arrow.** `friendly[].relocate` names a destination region, and
 * drawing a line to it needs the projection *and* the live zoom transform, both
 * of which live inside the vendored element and neither of which is on `api` or
 * on any event. A second projection computed beside it would agree on the first
 * frame and drift from the markers underneath it on the first pan — a line that
 * is wrong about where a unit is going is worse than a sentence that is right.
 * So the relocation is drawn where this console owns the pixels: on the marker's
 * own second line (`→ CALDIS · T7`, `model/scenario.ts`), in the same words the
 * selection rail's `RELOCATING` row uses, and in the roster.
 */

/** The subset of the vendored element's surface this wrapper drives. */
interface RegionMapElement extends HTMLElement {
  api?: {
    select?: (id: string | null) => void;
    focusUnit?: (id: string) => void;
    focusRegion?: (id: string) => void;
    setLayers?: (layers: Partial<MapLayers>) => void;
    setDim?: (ids: readonly string[] | null) => void;
    setPick?: (pick: { slot: string; ids: readonly string[] } | null) => void;
    setTerrain?: (on: boolean) => void;
    setHover?: (id: string | null) => void;
    fitSide?: (side: 'BLU' | 'OPF') => void;
    resetView?: () => void;
  };
  /** Not on `api` — see the header. Re-solves slots, then redraws markers. */
  geometry?: () => void;
  layout?: () => void;
  /** Set by the first `resize()`. Absent means the element cannot re-solve yet. */
  proj?: unknown;
}

export type MapContextKind = 'unit' | 'region' | 'empty';

export interface MapModifierClick {
  readonly id: string;
  readonly shift: boolean;
  readonly alt: boolean;
  readonly meta: boolean;
}

export interface RegionMapProps {
  readonly model: ScenarioModel;
  readonly layers: MapLayers;
  /** The selected asset or contact. A selected *region* is not a unit selection. */
  readonly selectedUnit: string | null;
  /** Ids matching the roster filter, or `null` for no filter. Dimmed, never hidden. */
  readonly dim: readonly string[] | null;
  /** The open task slot's candidates — a click on one fills the slot. */
  readonly pick: { readonly slot: string; readonly ids: readonly string[] } | null;
  readonly hover: string | null;
  /** Reveal, reset or fit — whatever the shell has asked the canvas for. */
  readonly command: MapCommand | null;
  /** The basemap this mount starts on. The canvas's own TER/VEC button owns it after that. */
  readonly terrain: boolean;

  readonly onSelect: (id: string | null) => void;
  readonly onRegion: (id: string | null) => void;
  readonly onPick: (slot: string, id: string) => void;
  readonly onContext: (kind: MapContextKind, id: string, x: number, y: number) => void;
  readonly onHover: (id: string | null) => void;
  readonly onModClick: (detail: MapModifierClick) => void;
  readonly onEngage: (actorId: string, targetId: string) => void;
  readonly onCommandApplied: (seq: number) => void;
  readonly onTerrainUnavailable: () => void;
  /** The operator pressed TER/VEC on the canvas; the store keeps it for the next mount. */
  readonly onTerrainChanged: (on: boolean) => void;
}

const TAG = 'region-map';

/**
 * The canvas legend opens **closed**, once, and then it is the operator's.
 *
 * `region-map.js` persists the choice in `localStorage` and defaults it *open*,
 * which in the prototype is a full-width canvas and here is a 250 px panel over
 * a 640 px pane at the console's minimum width. The rails already name every
 * unit's type in words beside its marker, so the legend is a reference rather
 * than a decoder ring — and a reference that covers a third of the map has cost
 * more than it explains. Seeded rather than forced: an operator who opens it
 * keeps it open, on this map and on every later one.
 */
function seedLegendClosed(): void {
  try {
    if (localStorage.getItem('bs-theater-legend') === null) {
      localStorage.setItem('bs-theater-legend', '0');
    }
  } catch {
    // A console with no storage still works; it just opens the legend.
  }
}

/**
 * Which units are placed, and where.
 *
 * The renderer sizes a region's slot set to the units that belong there and
 * indexes each unit into it, once. This string changes exactly when that answer
 * would — a unit appearing, being destroyed, or relocating — and nothing else in
 * a snapshot re-solves anything.
 */
function slotSignature(model: ScenarioModel): string {
  return model.UNITS.filter((unit) => unit.region !== undefined)
    .map((unit) => unit.region + '/' + unit.id)
    .join(',');
}

export function RegionMap(props: RegionMapProps): JSX.Element {
  const hostRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<RegionMapElement | null>(null);
  const [registered, setRegistered] = useState(regionMapRegistered);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let live = true;
    void ensureRegionMap().then(() => {
      if (live) setRegistered(regionMapRegistered());
    });
    return () => {
      live = false;
    };
  }, []);

  /**
   * The last id the *element* reported selecting.
   *
   * Without it the console fights itself: selecting a zone on the canvas emits
   * `bs-select null` and `bs-region <id>`, the store records a REGION selection,
   * and a `selectedUnit` effect that pushed unconditionally would answer with
   * `api.select(null)` — which clears the zone the operator just picked.
   */
  const echoed = useRef<string | null>(null);

  // Callbacks are read through a ref so that a parent re-rendering with new
  // closures does not tear the element down and rebuild it, which would throw
  // away the operator's zoom and pan on every snapshot.
  const handlers = useRef(props);
  handlers.current = props;

  const { model, terrain } = props;
  const initial = useRef({ model, terrain, layers: props.layers });

  useEffect(() => {
    const host = hostRef.current;
    if (host === null || !registered) return;

    // Seeded before the element is connected: `connectedCallback` reads both of
    // these to decide what it opens with.
    window.BS_MAP_TERRAIN = initial.current.terrain;
    seedLegendClosed();
    adopt(initial.current.model);

    const element = document.createElement(TAG) as RegionMapElement;
    element.style.width = '100%';
    element.style.height = '100%';
    element.setAttribute('data-testid', 'region-map-canvas');

    const onReady = (): void => setReady(true);
    const onSelect = (event: Event): void => {
      const id = (event as CustomEvent<string | null>).detail ?? null;
      echoed.current = id;
      handlers.current.onSelect(id);
    };
    const onRegion = (event: Event): void => {
      // `focusUnit`, `expandRegion` and a stack badge all report the region the
      // canvas moved to **while a unit is still selected**, and taking that as a
      // selection would swap the operator's subject for the ground under it.
      // `selectRegion` and `clearSelection` clear the unit first, so this reads
      // the element's own answer rather than guessing at intent.
      if (echoed.current !== null) return;
      handlers.current.onRegion((event as CustomEvent<string | null>).detail ?? null);
    };
    const onPick = (event: Event): void => {
      const detail = (event as CustomEvent<{ slot?: string; id?: string }>).detail;
      if (detail?.slot !== undefined && detail.id !== undefined) {
        handlers.current.onPick(detail.slot, detail.id);
      }
    };
    const onContext = (event: Event): void => {
      const detail = (
        event as CustomEvent<{
          kind: MapContextKind;
          id: string | null;
          x: number;
          y: number;
        }>
      ).detail;
      handlers.current.onContext(detail.kind, detail.id ?? '', detail.x, detail.y);
    };
    const onHover = (event: Event): void => {
      handlers.current.onHover((event as CustomEvent<string | null>).detail ?? null);
    };
    const onModClick = (event: Event): void => {
      handlers.current.onModClick((event as CustomEvent<MapModifierClick>).detail);
    };
    const onEngage = (event: Event): void => {
      const detail = (event as CustomEvent<{ actorId: string; targetId: string }>).detail;
      handlers.current.onEngage(detail.actorId, detail.targetId);
    };
    /**
     * A terrain tile that did not arrive (Q23's accepted degradation, made
     * visible).
     *
     * Captured rather than listened for: the tiles are `<image>` nodes the
     * renderer creates inside its own SVG, their `error` does not bubble, and
     * there is no callback on `api` for it. One failure is enough — the basemap
     * is a third-party service and the honest report is that it is not answering,
     * not a count of how often.
     */
    const onTileError = (event: Event): void => {
      if ((event.target as Element | null)?.tagName !== 'image') return;
      handlers.current.onTerrainUnavailable();
    };

    element.addEventListener('bs-map-ready', onReady);
    element.addEventListener('bs-select', onSelect);
    element.addEventListener('bs-region', onRegion);
    element.addEventListener('bs-pick', onPick);
    element.addEventListener('bs-context', onContext);
    element.addEventListener('bs-hover', onHover);
    element.addEventListener('bs-mod-click', onModClick);
    element.addEventListener('bs-engage', onEngage);
    element.addEventListener('error', onTileError, true);

    host.appendChild(element);
    elementRef.current = element;
    return () => {
      // The canvas owns the basemap toggle, so the answer comes back from the
      // global rather than from a prop — see `ConsoleState.mapTerrain`.
      handlers.current.onTerrainChanged(window.BS_MAP_TERRAIN !== false);
      element.removeEventListener('bs-map-ready', onReady);
      element.removeEventListener('bs-select', onSelect);
      element.removeEventListener('bs-region', onRegion);
      element.removeEventListener('bs-pick', onPick);
      element.removeEventListener('bs-context', onContext);
      element.removeEventListener('bs-hover', onHover);
      element.removeEventListener('bs-mod-click', onModClick);
      element.removeEventListener('bs-engage', onEngage);
      element.removeEventListener('error', onTileError, true);
      element.remove();
      elementRef.current = null;
      setReady(false);
    };
  }, [registered]);

  // A new model is a new snapshot. Adopting is enough to change what the next
  // draw reads; re-solving the slots is not, so it happens only when the set of
  // placed units has actually moved.
  const signature = useRef(slotSignature(model));
  useEffect(() => {
    adopt(model);
    const element = elementRef.current;
    if (element === null || !ready) return;
    const next = slotSignature(model);
    const moved = next !== signature.current;
    signature.current = next;
    if (moved && element.proj !== undefined) element.geometry?.();
    element.layout?.();
  }, [model, ready]);

  const layers = props.layers;
  useEffect(() => {
    if (ready) elementRef.current?.api?.setLayers?.(layers);
  }, [layers, ready]);

  const selectedUnit = props.selectedUnit;
  useEffect(() => {
    if (!ready || selectedUnit === echoed.current) return;
    echoed.current = selectedUnit;
    elementRef.current?.api?.select?.(selectedUnit);
  }, [selectedUnit, ready]);

  const dim = props.dim;
  useEffect(() => {
    if (ready) elementRef.current?.api?.setDim?.(dim);
  }, [dim, ready]);

  const pick = props.pick;
  useEffect(() => {
    if (ready) elementRef.current?.api?.setPick?.(pick);
  }, [pick, ready]);

  const hover = props.hover;
  useEffect(() => {
    if (ready) elementRef.current?.api?.setHover?.(hover);
  }, [hover, ready]);

  const command = props.command;
  const onCommandApplied = props.onCommandApplied;
  useEffect(() => {
    if (!ready || command === null) return;
    const api = elementRef.current?.api;
    switch (command.kind) {
      case 'UNIT':
        api?.focusUnit?.(command.id);
        break;
      case 'REGION':
        api?.focusRegion?.(command.id);
        break;
      case 'RESET':
        api?.resetView?.();
        break;
      case 'FIT':
        api?.fitSide?.(command.side);
        break;
    }
    onCommandApplied(command.seq);
  }, [command, ready, onCommandApplied]);

  useEffect(() => {
    if (ready) elementRef.current?.api?.setTerrain?.(terrain);
  }, [terrain, ready]);

  return (
    <div
      ref={hostRef}
      data-testid="region-map"
      data-ready={ready ? 'true' : 'false'}
      /*
       * `group`, not `application` or `img`. The canvas is a picture of data that is
       * fully present, named and operable in the roster and region rails beside it —
       * the vendored `<svg>` inside is `aria-hidden`, because thirty-odd unlabelled
       * pointer targets announced as bare graphics is worse than silence. What stays
       * reachable here is the view chrome the rails have no equivalent for: zoom, the
       * two-step fit, the basemap toggle and the legend.
       */
      role="group"
      aria-label="Theatre map view"
      style={{ flex: 1, minWidth: 0, minHeight: 0, position: 'relative' }}
    />
  );
}
